'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Trophy, ChevronRight, Flag, User, ChevronDown } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface LiveResult {
  id: string;
  position: number | null;
  driver_name: string;
  team: string;
  total_time: string;
  best_lap: string;
  gap: string;
  car_number: string;
  category: string;
  featured_on_homepage: boolean;
}

interface RaceEvent {
  id: string;
  name: string;
  date: string;
  venue: string;
  city: string;
  status: string;
  category: string;
  organizer_name?: string;
  organizer_id?: string;
}

interface AvatarMap {
  [driverName: string]: string | null;
}

interface ProfileMap {
  [driverName: string]: string | null;
}

interface NationalityMap {
  [driverName: string]: string | null;
}

interface CategoryResults {
  category: string;
  results: LiveResult[];
}

// ISO 3166-1 alpha-2 nationality lookup
function toISOCode(nationality: string): string | null {
  if (!nationality) return null;
  const n = nationality.trim().toLowerCase();
  const map: Record<string, string> = {
    // Common names
    'malaysian': 'MY', 'malaysia': 'MY',
    'singaporean': 'SG', 'singapore': 'SG',
    'indonesian': 'ID', 'indonesia': 'ID',
    'thai': 'TH', 'thailand': 'TH',
    'russian': 'RU', 'russia': 'RU',
    'british': 'GB', 'uk': 'GB', 'united kingdom': 'GB', 'england': 'GB',
    'american': 'US', 'usa': 'US', 'united states': 'US', 'us': 'US',
    'chinese': 'CN', 'china': 'CN',
    'japanese': 'JP', 'japan': 'JP',
    'korean': 'KR', 'south korea': 'KR', 'korea': 'KR',
    'australian': 'AU', 'australia': 'AU',
    'german': 'DE', 'germany': 'DE',
    'french': 'FR', 'france': 'FR',
    'italian': 'IT', 'italy': 'IT',
    'spanish': 'ES', 'spain': 'ES',
    'dutch': 'NL', 'netherlands': 'NL',
    'belgian': 'BE', 'belgium': 'BE',
    'swiss': 'CH', 'switzerland': 'CH',
    'swedish': 'SE', 'sweden': 'SE',
    'norwegian': 'NO', 'norway': 'NO',
    'danish': 'DK', 'denmark': 'DK',
    'finnish': 'FI', 'finland': 'FI',
    'polish': 'PL', 'poland': 'PL',
    'portuguese': 'PT', 'portugal': 'PT',
    'canadian': 'CA', 'canada': 'CA',
    'mexican': 'MX', 'mexico': 'MX',
    'brazilian': 'BR', 'brazil': 'BR',
    'argentinian': 'AR', 'argentina': 'AR',
    'indian': 'IN', 'india': 'IN',
    'pakistani': 'PK', 'pakistan': 'PK',
    'bangladeshi': 'BD', 'bangladesh': 'BD',
    'sri lankan': 'LK', 'sri lanka': 'LK',
    'nepali': 'NP', 'nepal': 'NP',
    'filipino': 'PH', 'philippines': 'PH',
    'vietnamese': 'VN', 'vietnam': 'VN',
    'cambodian': 'KH', 'cambodia': 'KH',
    'myanmar': 'MM', 'burmese': 'MM',
    'laotian': 'LA', 'laos': 'LA',
    'bruneian': 'BN', 'brunei': 'BN',
    'emirati': 'AE', 'uae': 'AE', 'united arab emirates': 'AE',
    'saudi': 'SA', 'saudi arabian': 'SA', 'saudi arabia': 'SA',
    'qatari': 'QA', 'qatar': 'QA',
    'kuwaiti': 'KW', 'kuwait': 'KW',
    'bahraini': 'BH', 'bahrain': 'BH',
    'omani': 'OM', 'oman': 'OM',
    'jordanian': 'JO', 'jordan': 'JO',
    'lebanese': 'LB', 'lebanon': 'LB',
    'turkish': 'TR', 'turkey': 'TR',
    'iranian': 'IR', 'iran': 'IR',
    'iraqi': 'IQ', 'iraq': 'IQ',
    'egyptian': 'EG', 'egypt': 'EG',
    'nigerian': 'NG', 'nigeria': 'NG',
    'south african': 'ZA', 'south africa': 'ZA',
    'kenyan': 'KE', 'kenya': 'KE',
    'ghanaian': 'GH', 'ghana': 'GH',
    'new zealander': 'NZ', 'new zealand': 'NZ',
    'austrian': 'AT', 'austria': 'AT',
    'czech': 'CZ', 'czech republic': 'CZ', 'czechia': 'CZ',
    'hungarian': 'HU', 'hungary': 'HU',
    'romanian': 'RO', 'romania': 'RO',
    'greek': 'GR', 'greece': 'GR',
    'ukrainian': 'UA', 'ukraine': 'UA',
    'taiwanese': 'TW', 'taiwan': 'TW',
    'hong kong': 'HK', 'hongkonger': 'HK',
    'macanese': 'MO', 'macau': 'MO',
    'mongolian': 'MN', 'mongolia': 'MN',
  };
  // Direct 2-letter ISO code passthrough
  if (/^[a-z]{2}$/.test(n)) return n.toUpperCase();
  return map[n] || null;
}

function parseTimeToMs(timeStr: string): number {
  if (!timeStr) return Infinity;
  const parts = timeStr.split(':');
  if (parts.length === 2) return (parseFloat(parts[0]) * 60 + parseFloat(parts[1])) * 1000;
  if (parts.length === 3) return (parseFloat(parts[0]) * 3600 + parseFloat(parts[1]) * 60 + parseFloat(parts[2])) * 1000;
  return Infinity;
}

function msToGap(ms: number): string {
  const totalSecs = ms / 1000;
  if (totalSecs < 60) return `+${totalSecs.toFixed(3)}`;
  const mins = Math.floor(totalSecs / 60);
  const secs = (totalSecs % 60).toFixed(3);
  return `+${mins}:${secs.padStart(6, '0')}`;
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
}

function DriverAvatar({ name, avatarUrl, size = 32 }: { name: string; avatarUrl?: string | null; size?: number }) {
  if (avatarUrl) {
    return (
      <img src={avatarUrl} alt={`${name} profile`} className="rounded-full object-cover border border-border shrink-0" style={{ width: size, height: size }} />
    );
  }
  return (
    <div className="rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <User size={size * 0.5} className="text-primary/60" />
    </div>
  );
}

function sortResultsByTime(rawResults: LiveResult[]): LiveResult[] {
  if (!rawResults || rawResults.length === 0) return [];
  const p1 = rawResults.find(r => r.gap === 'Leader' || r.position === 1);
  const p1Ms = p1 ? parseTimeToMs(p1.total_time) : 0;
  const sorted = [...rawResults].sort((a, b) => {
    if (a.gap === 'Leader') return -1;
    if (b.gap === 'Leader') return 1;
    const aMs = parseTimeToMs(a.total_time);
    const bMs = parseTimeToMs(b.total_time);
    if (aMs !== Infinity && bMs !== Infinity) return aMs - bMs;
    return (a.gap || '').localeCompare(b.gap || '');
  });
  return sorted.map((r, idx) => ({
    ...r,
    position: idx + 1,
    gap: idx === 0 ? 'Leader' : (parseTimeToMs(r.total_time) !== Infinity && p1Ms > 0
      ? msToGap(parseTimeToMs(r.total_time) - p1Ms)
      : r.gap),
  }));
}

const positionStyles: Record<number, { border: string; bg: string; color: string; height: string }> = {
  1: { border: 'border-gold', bg: 'bg-gold/10', color: 'text-gold', height: 'h-28' },
  2: { border: 'border-silver', bg: 'bg-silver/10', color: 'text-silver', height: 'h-20' },
  3: { border: 'border-bronze', bg: 'bg-bronze/10', color: 'text-bronze', height: 'h-16' },
};

function CategoryResultTable({ category, results, avatarMap, profileMap, nationalityMap, eventName }: { category: string; results: LiveResult[]; avatarMap: AvatarMap; profileMap: ProfileMap; nationalityMap: NationalityMap; eventName: string }) {
  const top3 = results.filter(r => r.position !== null && (r.position as number) <= 3).sort((a, b) => (a.position as number) - (b.position as number));
  const podiumOrder = [top3[1], top3[0], top3[2]];
  const rest = results.filter(r => r.position !== null && (r.position as number) > 3 && (r.position as number) <= 6);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden mb-6">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-positive/10 flex items-center justify-center">
            <Flag size={16} className="text-positive" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">{eventName}</p>
            <p className="text-xs text-muted-foreground">Category: <span className="font-bold text-primary">{category}</span></p>
          </div>
        </div>
        <span className="px-3 py-1 rounded-full bg-primary/10 border border-primary/30 text-xs font-bold uppercase tracking-widest text-primary">{category}</span>
      </div>

      {results.length === 0 ? (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-muted-foreground">No results for this category yet.</p>
        </div>
      ) : (
        <div className="px-5 py-6">
          {/* Podium visual */}
          {top3.length >= 1 && (
            <div className="flex items-end justify-center gap-3 mb-6">
              {podiumOrder.map((driver) => {
                if (!driver) return null;
                const pos = driver.position as number;
                const style = positionStyles[pos];
                if (!style) return null;
                const avatar = avatarMap[driver.driver_name];
                const isoCode = nationalityMap[driver.driver_name] ? toISOCode(nationalityMap[driver.driver_name]!) : null;
                return (
                  <div key={`podium-${driver.id}`} className="flex flex-col items-center gap-2 w-28 md:w-36">
                    <div className="text-center mb-2">
                      <div className="flex justify-center mb-1.5">
                        <DriverAvatar name={driver.driver_name} avatarUrl={avatar} size={32} />
                      </div>
                      {profileMap[driver.driver_name] ? (
                        <a href={`/driver/${profileMap[driver.driver_name]}`} className="text-xs font-black text-foreground uppercase tracking-wider break-all leading-tight max-w-[6rem] mx-auto hover:text-primary hover:underline block">
                          {driver.driver_name.split(' ')[0]}
                        </a>
                      ) : (
                        <p className="text-xs font-black text-foreground uppercase tracking-wider break-all leading-tight max-w-[6rem] mx-auto">
                          {driver.driver_name.split(' ')[0]}
                        </p>
                      )}
                      {isoCode && <p className="text-xs font-bold text-muted-foreground mt-0.5">{isoCode}</p>}
                      <p className="text-xs font-bold font-tabular text-muted-foreground mt-1">{driver.total_time}</p>
                    </div>
                    <div className={`w-full rounded-t-lg border-t-2 ${style.border} ${style.bg} ${style.height} flex items-center justify-center`}>
                      <div className="text-center">
                        {pos === 1 && <Trophy size={16} className={style.color} />}
                        <p className={`text-3xl font-black ${style.color} font-tabular tracking-tight`}>P{pos}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Positions 4–6 */}
          {rest.length > 0 && (
            <div className="space-y-2">
              {rest.map((result) => {
                const avatar = avatarMap[result.driver_name];
                const isoCode = nationalityMap[result.driver_name] ? toISOCode(nationalityMap[result.driver_name]!) : null;
                return (
                  <div key={`result-row-${result.id}`} className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold font-tabular text-muted-foreground w-6 text-center">P{result.position}</span>
                      <DriverAvatar name={result.driver_name} avatarUrl={avatar} size={26} />
                      <div>
                        {profileMap[result.driver_name] ? (
                          <a href={`/driver/${profileMap[result.driver_name]}`} className="text-sm font-semibold text-foreground hover:text-primary hover:underline break-words max-w-[140px] block">{result.driver_name}</a>
                        ) : (
                          <p className="text-sm font-semibold text-foreground break-words max-w-[140px]">{result.driver_name}</p>
                        )}
                        {isoCode && <p className="text-xs font-bold text-muted-foreground">{isoCode}</p>}
                      </div>
                      <p className="text-xs text-muted-foreground">{result.team}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold font-tabular text-foreground">{result.gap}</p>
                      <p className="text-xs text-muted-foreground">Best: {result.best_lap}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function LatestResultsPodium() {
  const supabase = createClient();
  const router = useRouter();
  const [events, setEvents] = useState<RaceEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [event, setEvent] = useState<RaceEvent | null>(null);
  const [categoryResults, setCategoryResults] = useState<CategoryResults[]>([]);
  const [loading, setLoading] = useState(true);
  const [avatarMap, setAvatarMap] = useState<AvatarMap>({});
  const [profileMap, setProfileMap] = useState<ProfileMap>({});
  const [nationalityMap, setNationalityMap] = useState<NationalityMap>({});
  const [organizers, setOrganizers] = useState<string[]>([]);
  const [selectedOrganizer, setSelectedOrganizer] = useState('');

  useEffect(() => {
    const fetchEvents = async () => {
      // Run both queries in parallel
      const [settingsRes, eventsRes] = await Promise.all([
        supabase.from('app_settings').select('broadcast_event_id').eq('key', 'broadcast_event_id').maybeSingle(),
        supabase.from('race_events').select('id, name, date, venue, city, status, category, organizer_name, organizer_id').order('date', { ascending: false }),
      ]);

      const data = eventsRes.data;
      if (data && data.length > 0) {
        setEvents(data as RaceEvent[]);

        // Build organizer list
        const orgNames = [...new Set((data as RaceEvent[]).map(e => e.organizer_name).filter(Boolean))] as string[];
        setOrganizers(orgNames);

        // Use broadcast event if set, otherwise use latest
        const broadcastId = settingsRes.data?.broadcast_event_id;
        if (broadcastId && data.find((e: any) => e.id === broadcastId)) {
          setSelectedEventId(broadcastId);
        } else {
          setSelectedEventId(data[0].id);
        }
      }
      setLoading(false);
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    if (!selectedEventId) return;
    const evt = events.find(e => e.id === selectedEventId);
    setEvent(evt || null);

    const fetchResults = async () => {
      const { data } = await supabase
        .from('live_results')
        .select('id, position, driver_name, team, total_time, best_lap, gap, car_number, category, featured_on_homepage')
        .eq('event_id', selectedEventId);

      if (data && data.length > 0) {
        const allResults = data as LiveResult[];

        // Get categories from event definition first, then from results
        const evtCats = (evt?.category || '').split(',').map((c: string) => c.trim()).filter(Boolean);
        const resultCats = [...new Set(allResults.map((r: any) => r.category).filter(Boolean))];
        const allCats = evtCats.length > 0 ? evtCats : resultCats.length > 0 ? resultCats : ['General'];

        // Build per-category sorted results
        const catResults: CategoryResults[] = allCats.map(cat => {
          const catData = allResults.filter(r => r.category === cat || (!r.category && cat === 'General'));
          return {
            category: cat,
            results: sortResultsByTime(catData),
          };
        }).filter(cr => cr.results.length > 0);

        setCategoryResults(catResults);

        // Fetch avatars, profile links, and nationality via event_registrations (race_name → user_id)
        const driverNames = allResults.map(r => r.driver_name).filter(Boolean);
        if (driverNames.length > 0 && selectedEventId) {
          const { data: regs } = await supabase
            .from('event_registrations')
            .select('race_name, user_id, nationality, user_profiles(avatar_url)')
            .eq('event_id', selectedEventId)
            .in('race_name', driverNames);
          const amap: AvatarMap = {};
          const pmap: ProfileMap = {};
          const nmap: NationalityMap = {};
          if (regs) {
            regs.forEach((reg: any) => {
              if (reg.race_name) {
                pmap[reg.race_name] = reg.user_id || null;
                amap[reg.race_name] = reg.user_profiles?.avatar_url || null;
                nmap[reg.race_name] = reg.nationality || null;
              }
            });
          }
          setAvatarMap(amap);
          setProfileMap(pmap);
          setNationalityMap(nmap);
        } else {
          setAvatarMap({});
          setProfileMap({});
          setNationalityMap({});
        }
      } else {
        setCategoryResults([]);
      }
    };
    fetchResults();
  }, [selectedEventId, events]);

  const handleViewFullResults = () => {
    router.push(`/live-results?event=${selectedEventId}`);
  };

  // Filter events by organizer
  const filteredEvents = selectedOrganizer
    ? events.filter(e => e.organizer_name === selectedOrganizer)
    : events;

  if (loading) {
    return (
      <section className="py-20 px-4 lg:px-8 xl:px-10 max-w-screen-2xl mx-auto">
        <div className="flex items-center justify-center py-16">
          <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  if (!event || categoryResults.length === 0) {
    return (
      <section className="py-20 px-4 lg:px-8 xl:px-10 max-w-screen-2xl mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Latest Race</p>
            <h2 className="text-3xl md:text-4xl font-extrabold uppercase tracking-tight text-foreground">Latest Results</h2>
          </div>
          <Link href="/live-results" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors">
            Full Results <ChevronRight size={16} />
          </Link>
        </div>
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Flag size={40} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-semibold text-muted-foreground">No results available yet</p>
          <p className="text-xs text-muted-foreground mt-1">Results will appear here after the first race.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 px-4 lg:px-8 xl:px-10 max-w-screen-2xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
            {event.date ? formatDate(event.date) : 'Latest Race'}
          </p>
          <h2 className="text-2xl md:text-4xl font-extrabold uppercase tracking-tight text-foreground">Latest Results</h2>
          {event.organizer_name && (
            <p className="text-sm text-muted-foreground mt-1">
              Organized by{' '}
              {event.organizer_id ? (
                <Link
                  href={`/organizer-profile/${event.organizer_id}`}
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  {event.organizer_name}
                </Link>
              ) : (
                <span className="text-primary font-semibold">{event.organizer_name}</span>
              )}
            </p>
          )}
        </div>
        <button onClick={handleViewFullResults} className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors">
          Full Results <ChevronRight size={16} />
        </button>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Event Dropdown */}
        {filteredEvents.length > 1 && (
          <div className="relative">
            <select
              value={selectedEventId}
              onChange={e => setSelectedEventId(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 rounded-md bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer"
            >
              {filteredEvents.map(e => (
                <option key={e.id} value={e.id}>{e.name} ({e.date})</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        )}

        {/* Organizer Dropdown */}
        {organizers.length > 1 && (
          <div className="relative">
            <select
              value={selectedOrganizer}
              onChange={e => {
                setSelectedOrganizer(e.target.value);
                // Auto-select first event of that organizer
                const orgEvts = e.target.value ? events.filter(ev => ev.organizer_name === e.target.value) : events;
                if (orgEvts.length > 0) setSelectedEventId(orgEvts[0].id);
              }}
              className="appearance-none pl-3 pr-8 py-2 rounded-md bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer"
            >
              <option value="">All Organizers</option>
              {organizers.map(org => (
                <option key={org} value={org}>{org}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        )}
      </div>

      {/* Per-category result tables */}
      {categoryResults.map(cr => (
        <CategoryResultTable
          key={cr.category}
          category={cr.category}
          results={cr.results}
          avatarMap={avatarMap}
          profileMap={profileMap}
          nationalityMap={nationalityMap}
          eventName={event.name}
        />
      ))}

      <div className="mt-4">
        <button
          onClick={handleViewFullResults}
          className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-bold uppercase tracking-wide rounded-md btn-secondary"
        >
          View Full Results Table <ChevronRight size={16} />
        </button>
      </div>
    </section>
  );
}